/**
 * ボトムシートコンポーネント
 * モバイルのアクションシート用。Headless UI Dialog ベース。
 * 閉じアニメーション完了後に onClose を呼ぶ。
 * ドラッグハンドル or スクロール末端から下スワイプで閉じられる。
 *
 * 内部に独自のドラッグ操作を持つUI（画像のトリミング枠、スライダー等）は、
 * その要素に `data-sheet-no-drag` を付けると、そこ起点のタッチではシートが動かなくなる。
 * つまみからのドラッグでは従来どおり閉じられる。
 */

import { type FC, type ReactNode, useState, useCallback, useRef, useEffect } from 'react';
import { Dialog, DialogPanel } from '@headlessui/react';
import { BottomSheetCloseContext } from '@/hooks/useBottomSheetClose';
import { useAllowPinchZoom } from '@/hooks/useAllowPinchZoom';
import { usePageTheme } from '@/hooks/usePageTheme';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  /**
   * ユーザー操作（背景タップ・ドラッグ）で close が始まる直前に呼ばれる。
   * false を返すとその close をブロックする（閉じアニメーションを走らせない）。
   * 親側の onClose（プログラム的な isOpen=false）には影響しない。
   */
  onBeforeClose?: () => boolean;
}

const DURATION_MS = 300;
/** シートの高さの何%を超えたら閉じるか */
const CLOSE_THRESHOLD = 0.3;

/**
 * タッチ対象からコンテナまでの間で、最も近いスクロール可能な要素を探す。
 * touchstart 時に1回だけ呼び出し、結果を touchmove で参照する。
 */
function findNearestScrollable(
  target: HTMLElement | null,
  container: HTMLElement,
): HTMLElement | null {
  let el = target;
  while (el && el !== container) {
    const { overflowY } = getComputedStyle(el);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      el.scrollHeight > el.clientHeight + 1
    ) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

export const BottomSheet: FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  children,
  onBeforeClose,
}) => {
  const pageTheme = usePageTheme();
  const [closing, setClosing] = useState(false);
  const show = isOpen || closing;
  const panelNodeRef = useRef<HTMLDivElement | null>(null);
  const closingGuardRef = useRef(false);

  // ドラッグ状態（ハンドル・スクロール末端共通）
  const dragStartYRef = useRef<number | null>(null);
  const dragCurrentYRef = useRef(0);
  const backdropRef = useRef<HTMLDivElement | null>(null);
  // スクロール末端ドラッグ: コンテンツ領域からのドラッグかどうか
  const isContentDragRef = useRef(false);
  // スクロール末端ドラッグ: 末端到達時のタッチY座標
  const scrollDragAnchorRef = useRef<number | null>(null);

  // Headless UI Dialog が documentElement.style.overflow で
  // スクロールロックを管理するため、useScrollLock は不要

  // === 閉じアニメーション ===

  const startClose = useCallback(() => {
    const node = panelNodeRef.current;
    if (!node || closingGuardRef.current) return;
    closingGuardRef.current = true;

    setClosing(true);
    node.style.transition = `transform ${DURATION_MS}ms cubic-bezier(0.36, 0, 0.66, -0.5)`;
    node.style.transform = 'translateY(100%)';

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      closingGuardRef.current = false;
      setClosing(false);
      onClose();
    };

    node.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, DURATION_MS + 50);
  }, [onClose]);

  const startCloseFromDrag = useCallback(() => {
    const node = panelNodeRef.current;
    if (!node || closingGuardRef.current) return;
    closingGuardRef.current = true;

    setClosing(true);
    node.style.transition = `transform ${DURATION_MS}ms ease-out`;
    node.style.transform = 'translateY(100%)';

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      closingGuardRef.current = false;
      setClosing(false);
      onClose();
    };

    node.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, DURATION_MS + 50);
  }, [onClose]);

  // === パネルドラッグ共通処理 ===

  const applyDrag = useCallback((dy: number) => {
    const clampedDy = Math.max(0, dy);
    dragCurrentYRef.current = clampedDy;

    const node = panelNodeRef.current;
    if (node) {
      node.style.transform = `translateY(${clampedDy}px)`;
    }
    if (backdropRef.current && node) {
      const progress = clampedDy / node.offsetHeight;
      backdropRef.current.style.opacity = String(1 - progress);
    }
  }, []);

  const snapBack = useCallback(() => {
    const node = panelNodeRef.current;
    if (!node) return;
    node.style.transition = `transform ${DURATION_MS}ms cubic-bezier(0.22, 1.12, 0.36, 1)`;
    node.style.transform = 'translateY(0)';
    if (backdropRef.current) {
      backdropRef.current.style.opacity = '1';
    }
  }, []);

  const finishDrag = useCallback(() => {
    const node = panelNodeRef.current;
    if (!node) return;

    const dragDistance = dragCurrentYRef.current;

    // ドラッグが実質起きていない場合（タップ等）はスキップ
    if (dragDistance < 1) return;

    const sheetHeight = node.offsetHeight;

    if (dragDistance > sheetHeight * CLOSE_THRESHOLD) {
      if (onBeforeClose && !onBeforeClose()) {
        snapBack();
      } else {
        startCloseFromDrag();
      }
    } else {
      snapBack();
    }
  }, [onBeforeClose, snapBack, startCloseFromDrag]);

  // === ハンドルのドラッグ ===

  const handleDragStart = useCallback((e: React.TouchEvent) => {
    isContentDragRef.current = false;
    // 2本指以上はピンチ（拡大縮小）。シートのドラッグとして扱わない
    if (e.touches.length > 1) {
      dragStartYRef.current = null;
      return;
    }
    dragStartYRef.current = e.touches[0].clientY;
    dragCurrentYRef.current = 0;
    const node = panelNodeRef.current;
    if (node) {
      node.style.transition = 'none';
    }
  }, []);

  const handleDragMove = useCallback((e: React.TouchEvent) => {
    if (dragStartYRef.current === null) return;
    if (e.touches.length > 1) {
      dragStartYRef.current = null;
      dragCurrentYRef.current = 0;
      snapBack();
      return;
    }
    const dy = e.touches[0].clientY - dragStartYRef.current;
    applyDrag(dy);
  }, [applyDrag, snapBack]);

  const handleDragEnd = useCallback(() => {
    if (dragStartYRef.current === null) return;
    dragStartYRef.current = null;
    finishDrag();
  }, [finishDrag]);

  // === コンテンツ領域: スクロール末端からのドラッグ ===
  //
  // 設計:
  //   1. touchstart でタッチ対象の最寄りネストスクロール要素を1回だけ特定
  //   2. touchmove でそのプロパティ (scrollTop) を読むだけ — DOM走査なし
  //   3. ネスト要素がスクロール可能ならパネルドラッグを抑制
  //   4. すべてのスクロール要素が境界に達した場合のみパネルドラッグに移行

  // コールバックをrefに保持して、useEffect内から最新版を参照する
  const applyDragRef = useRef(applyDrag);
  const finishDragRef = useRef(finishDrag);
  const snapBackRef = useRef(snapBack);
  useEffect(() => {
    applyDragRef.current = applyDrag;
    finishDragRef.current = finishDrag;
    snapBackRef.current = snapBack;
  });

  useEffect(() => {
    const node = panelNodeRef.current;
    if (!node) return;

    // ジェスチャーごとにネストスクロール要素を保持（touchstart で設定）
    let nestedScrollable: HTMLElement | null = null;

    const onTouchStart = (e: TouchEvent) => {
      isContentDragRef.current = false;
      scrollDragAnchorRef.current = null;
      // 2本指以上はピンチ（拡大縮小）。シートのドラッグとして扱わない
      if (e.touches.length > 1) {
        dragStartYRef.current = null;
        return;
      }
      // 独自のドラッグを持つ領域（画像のトリミング枠など）では、
      // シートを掴まない。掴むと指1本の操作をシートと取り合ってしまう
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-sheet-no-drag]')) {
        dragStartYRef.current = null;
        return;
      }
      dragStartYRef.current = e.touches[0].clientY;
      dragCurrentYRef.current = 0;
      nestedScrollable = findNearestScrollable(e.target as HTMLElement | null, node);
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!node || dragStartYRef.current === null) return;

      // ブラウザが既にスクロールを始めている（cancelable=false）ときは手を出さない。
      // preventDefault が効かないので、シートのドラッグと画面のスクロールが同時に走り、
      // その状態で指を離してシートが閉じると、スクロール中のジェスチャーを掴んだまま
      // DOM が消えて画面がスクロールできなくなることがある（Chrome）。
      if (!e.cancelable) {
        if (isContentDragRef.current) {
          isContentDragRef.current = false;
          scrollDragAnchorRef.current = null;
          dragCurrentYRef.current = 0;
          snapBackRef.current();
        }
        dragStartYRef.current = null;
        return;
      }

      const touchY = e.touches[0].clientY;
      const dy = touchY - dragStartYRef.current;

      // パネルドラッグ中: シートを指に追従させる
      if (isContentDragRef.current) {
        const anchor = scrollDragAnchorRef.current ?? touchY;
        const panelDy = touchY - anchor;
        applyDragRef.current(panelDy);
        e.preventDefault();
        return;
      }

      // 下方向ドラッグのみ処理（上方向はスクロールに任せる）
      if (dy > 10) {
        // ネスト要素がまだ上にスクロール可能ならそちらに任せる
        if (nestedScrollable && nestedScrollable.scrollTop > 0) {
          return;
        }

        // 下方向ドラッグ = コンテンツを上へスクロールする操作。
        // スクロールが上端に達している（これ以上上へスクロールできない）か、
        // そもそもスクロール不可のときだけパネルドラッグ（閉じる動作）を開始する。
        // 末端(isAtBottom)を含めると、最下部から上スクロールしようとした瞬間に
        // シートを掴んでしまい、意図せず閉じてしまう。
        const isAtTop = node.scrollTop < 1;
        const isNotScrollable = node.scrollHeight <= node.clientHeight + 1;

        if (isAtTop || isNotScrollable) {
          isContentDragRef.current = true;
          scrollDragAnchorRef.current = touchY;
          node.style.transition = 'none';
          e.preventDefault();
        }
      }
    };

    const onTouchEnd = () => {
      if (isContentDragRef.current) {
        isContentDragRef.current = false;
        scrollDragAnchorRef.current = null;
        // finishDrag は dragCurrentY > 0 の場合のみ実効的に動作する
        finishDragRef.current();
      }
      dragStartYRef.current = null;
      dragCurrentYRef.current = 0;
    };

    node.addEventListener('touchstart', onTouchStart, { passive: true });
    node.addEventListener('touchmove', onTouchMove, { passive: false });
    node.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      node.removeEventListener('touchstart', onTouchStart);
      node.removeEventListener('touchmove', onTouchMove);
      node.removeEventListener('touchend', onTouchEnd);
      // 閉じるときにドラッグ状態を持ち越さない（次に開いたシートが
      // いきなりドラッグ中として振る舞うのを防ぐ）
      isContentDragRef.current = false;
      scrollDragAnchorRef.current = null;
      dragStartYRef.current = null;
      dragCurrentYRef.current = 0;
    };
  }, [show]);

  // === ピンチ（拡大縮小）を塞がないようにする ===
  // 片手ドラッグの途中で2本目の指が置かれたら、ドラッグは取り消して元に戻す
  const cancelDragForPinch = useCallback(() => {
    if (!isContentDragRef.current && dragStartYRef.current === null) return;
    isContentDragRef.current = false;
    scrollDragAnchorRef.current = null;
    dragStartYRef.current = null;
    dragCurrentYRef.current = 0;
    snapBackRef.current();
  }, []);
  useAllowPinchZoom(show, cancelDragForPinch);

  // === 開くアニメーション ===

  const panelRef = useCallback(
    (node: HTMLDivElement | null) => {
      panelNodeRef.current = node;
      if (node && isOpen) {
        node.style.transition = `transform ${DURATION_MS}ms cubic-bezier(0.50, 0.00, 0.65, 1.4)`;
        node.style.transform = 'translateY(100%)';
        void node.offsetHeight;
        node.style.transform = 'translateY(0)';
      }
    },
    [isOpen]
  );

  /**
   * ユーザー操作による close の入口（背景タップ・Escape・シート内の「キャンセル」）。
   * onBeforeClose で止められたらアニメーションを始めない。
   */
  const requestClose = useCallback(() => {
    if (onBeforeClose && !onBeforeClose()) return;
    startClose();
  }, [onBeforeClose, startClose]);

  if (!show) return null;

  return (
    <BottomSheetCloseContext.Provider value={requestClose}>
      <Dialog open={true} onClose={requestClose} className="relative z-50">
        {/* 背景オーバーレイ */}
        <div
          ref={backdropRef}
          className="fixed inset-0 bg-black/30 backdrop-blur-sm"
          style={{
            opacity: closing ? 0 : 1,
            transition: closing ? `opacity ${DURATION_MS}ms ease-out` : undefined,
          }}
          aria-hidden="true"
        />

        <div className="fixed inset-0 flex items-end justify-center pointer-events-none">
          <DialogPanel
            ref={panelRef}
            className="bottom-sheet-panel w-full max-w-lg bg-white rounded-t-2xl shadow-xl max-h-[90dvh] overflow-y-auto pointer-events-auto"
            style={{
              ...pageTheme,
              transition: 'none',
              boxShadow: '0 50px 0 50px white, 0 -25px 50px -12px rgba(0,0,0,0.25)',
            }}
          >
            {/* ドラッグハンドル */}
            <div
              className="bottom-sheet-handle flex justify-center pt-3 pb-3 sticky top-0 bg-white rounded-t-2xl cursor-grab active:cursor-grabbing"
              onTouchStart={handleDragStart}
              onTouchMove={handleDragMove}
              onTouchEnd={handleDragEnd}
            >
              <div className="w-10 h-1.5 bg-text-300 rounded-full" />
            </div>

            {children}

            {/* iOS Safari ナビゲーションバーとの重なり防止 */}
            <div className="h-[env(safe-area-inset-bottom)]" />
          </DialogPanel>
        </div>
      </Dialog>
    </BottomSheetCloseContext.Provider>
  );
};
